import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Spinner } from '@/components/ui/Spinner'
import { Pagination } from '@/components/ui/Pagination'
import { EmptyState } from '@/components/ui/EmptyState'
import { Badge } from '@/components/ui/Badge'
import { useTickets, useTicketStats } from '@/hooks/useTickets'
import { useDebounce } from '@/hooks/useDebounce'
import {
  Ticket as TicketIcon,
  Search,
  AlertCircle,
  CheckCircle2,
  Clock,
  MessageSquare
} from 'lucide-react'
import type { TicketStatus, TicketPriority } from '@/types/database'
import { formatDistanceToNow } from 'date-fns'
import { fr } from 'date-fns/locale'

const ITEMS_PER_PAGE = 20

const statusConfig: Record<TicketStatus, { label: string; color: 'mint' | 'gold' | 'brown' }> = {
  open: { label: 'Ouvert', color: 'mint' },
  in_progress: { label: 'En cours', color: 'gold' },
  closed: { label: 'Fermé', color: 'brown' },
}

const priorityConfig: Record<TicketPriority, { label: string; color: 'mint' | 'gold' | 'brown' }> = {
  low: { label: 'Basse', color: 'mint' },
  medium: { label: 'Moyenne', color: 'gold' },
  high: { label: 'Haute', color: 'brown' },
}

export default function TicketsPage() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<TicketStatus | ''>('')
  const [priorityFilter, setPriorityFilter] = useState<TicketPriority | ''>('')
  const [page, setPage] = useState(1)

  const debouncedSearch = useDebounce(search, 300)

  const { data, isLoading } = useTickets({
    search: debouncedSearch,
    status: statusFilter || undefined,
    priority: priorityFilter || undefined,
    page,
    limit: ITEMS_PER_PAGE,
  })

  const { data: stats } = useTicketStats()

  const totalPages = Math.ceil((data?.total || 0) / ITEMS_PER_PAGE)

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-brown-900">
            Tickets de support
          </h1>
          <p className="mt-1 text-brown-600">
            {data?.total || 0} ticket{(data?.total || 0) > 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card padding="sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-brown-600">Total</p>
                <p className="text-2xl font-bold text-brown-900">{stats.total}</p>
              </div>
              <TicketIcon className="w-8 h-8 text-brown-400" />
            </div>
          </Card>
          <Card padding="sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-brown-600">Ouverts</p>
                <p className="text-2xl font-bold text-mint-600">{stats.open}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-mint-500" />
            </div>
          </Card>
          <Card padding="sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-brown-600">En cours</p>
                <p className="text-2xl font-bold text-gold-600">{stats.in_progress}</p>
              </div>
              <Clock className="w-8 h-8 text-gold-500" />
            </div>
          </Card>
          <Card padding="sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-brown-600">Fermés</p>
                <p className="text-2xl font-bold text-brown-600">{stats.closed}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-brown-400" />
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 min-w-[200px]">
            <Input
              type="search"
              placeholder="Rechercher un ticket..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as TicketStatus | '')
                setPage(1)
              }}
            >
              <option value="">Tous les statuts</option>
              <option value="open">Ouvert</option>
              <option value="in_progress">En cours</option>
              <option value="closed">Fermé</option>
            </Select>
          </div>
          <div className="w-full sm:w-48">
            <Select
              value={priorityFilter}
              onChange={(e) => {
                setPriorityFilter(e.target.value as TicketPriority | '')
                setPage(1)
              }}
            >
              <option value="">Toutes les priorités</option>
              <option value="low">Basse</option>
              <option value="medium">Moyenne</option>
              <option value="high">Haute</option>
            </Select>
          </div>
        </div>
      </Card>

      {/* Tickets list */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : !data?.tickets.length ? (
        <EmptyState
          icon={<TicketIcon className="w-12 h-12" />}
          title="Aucun ticket"
          description="Il n'y a aucun ticket pour le moment."
        />
      ) : (
        <>
          <Card padding="none">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-brown-200">
                    <th className="px-6 py-3 text-left text-xs font-medium text-brown-600 uppercase tracking-wider">
                      Ticket
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brown-600 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brown-600 uppercase tracking-wider">
                      Assigné à
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brown-600 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brown-600 uppercase tracking-wider">
                      Priorité
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brown-600 uppercase tracking-wider">
                      Messages
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-brown-600 uppercase tracking-wider">
                      Dernière activité
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brown-200">
                  {data.tickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      className="hover:bg-brown-50 transition-colors cursor-pointer"
                      onClick={() => window.location.href = `/tickets/${ticket.id}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-start gap-3">
                          <TicketIcon className="w-5 h-5 text-brown-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-brown-900">{ticket.title}</p>
                            <p className="text-sm text-brown-600 line-clamp-1">
                              {ticket.description}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <p className="font-medium text-brown-900">
                            {ticket.user?.full_name || ticket.user?.email}
                          </p>
                          {ticket.project && (
                            <p className="text-brown-600">{ticket.project.title}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {ticket.assigned_admin ? (
                          <div className="text-sm text-brown-700">
                            {ticket.assigned_admin.full_name || ticket.assigned_admin.email}
                          </div>
                        ) : (
                          <span className="text-sm text-brown-400">Non assigné</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <Badge color={statusConfig[ticket.status].color}>
                          {statusConfig[ticket.status].label}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <Badge color={priorityConfig[ticket.priority].color}>
                          {priorityConfig[ticket.priority].label}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-brown-600">
                          <MessageSquare className="w-4 h-4" />
                          {ticket.message_count || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-brown-600">
                        {formatDistanceToNow(new Date(ticket.last_message_at || ticket.updated_at), {
                          addSuffix: true,
                          locale: fr,
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}
